import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Video,
  MessageSquare,
  BarChart3,
  Loader2,
  RefreshCw,
  ExternalLink,
  Play,
  ThumbsUp,
  MessageCircle,
  CheckSquare,
  Square,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  History,
} from 'lucide-react';
import { cn, formatTime } from '@/utils/helpers';
import { videoAPI } from '@/services/api';
import { useAuthStore, useChatStore } from '@/store';
import { Message } from '@/components/Message';
import { Sidebar } from '@/components/Sidebar';
import type { SimilarUserVideo, ExtractedLeadsRecord, LeadAnalysisRecord } from '@/types';

type TabKey = 'similar' | 'hot' | 'leads' | 'analysis' | 'sessions';

interface SimilarUser {
  id: string;
  sec_uid: string;
  nickname: string;
  avatar?: string;
  signature?: string;
  follower_count?: number;
  aweme_count?: number;
  created_at: number;
}

export function ContentPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const sessions = useChatStore((state) => state.sessions);
  const [activeTab, setActiveTab] = useState<TabKey>('similar');

  // 从 URL query 参数读取 tab
  useEffect(() => {
    const tab = searchParams.get('tab') as TabKey;
    if (tab && tabs.some((t) => t.key === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // 数据状态
  const [similarUsers, setSimilarUsers] = useState<SimilarUser[]>([]);
  const [hotVideos, setHotVideos] = useState<SimilarUserVideo[]>([]);
  const [leads, setLeads] = useState<ExtractedLeadsRecord[]>([]);
  const [analyses, setAnalyses] = useState<LeadAnalysisRecord[]>([]);

  // 加载状态
  const [loading, setLoading] = useState<Record<TabKey, boolean>>({
    similar: false,
    hot: false,
    leads: false,
    analysis: false,
    sessions: false,
  });
  const [loadError, setLoadError] = useState<Record<TabKey, string>>({
    similar: '',
    hot: '',
    leads: '',
    analysis: '',
    sessions: '',
  });

  // 热门视频批量选择
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [batchExtracting, setBatchExtracting] = useState(false);

  // 展开状态
  const [expandedLeads, setExpandedLeads] = useState<Set<string>>(new Set());
  const [expandedAnalyses, setExpandedAnalyses] = useState<Set<string>>(new Set());
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  const tabs = [
    { key: 'similar' as TabKey, label: '同类账号', icon: Users },
    { key: 'hot' as TabKey, label: '热门视频', icon: Video },
    { key: 'leads' as TabKey, label: '有效评论', icon: MessageSquare },
    { key: 'analysis' as TabKey, label: '评论分析', icon: BarChart3 },
    { key: 'sessions' as TabKey, label: '会话记录', icon: History },
  ];

  const loadData = useCallback(async (tab: TabKey) => {
    setLoading((prev) => ({ ...prev, [tab]: true }));
    setLoadError((prev) => ({ ...prev, [tab]: '' }));
    try {
      switch (tab) {
        case 'sessions': {
          // 本地会话数据，无需异步加载
          break;
        }
        case 'similar': {
          const res = await videoAPI.getMySimilarUsers(50);
          if (res.success && res.data) {
            setSimilarUsers(res.data.users || []);
          }
          break;
        }
        case 'hot': {
          const res = await videoAPI.getSavedHotVideos(100);
          if (res.success && res.data) {
            setHotVideos(res.data.videos || []);
          }
          break;
        }
        case 'leads': {
          const res = await videoAPI.getSavedLeads(50);
          if (res.success && res.data) {
            setLeads(res.data.leads || []);
          }
          break;
        }
        case 'analysis': {
          const res = await videoAPI.getSavedAnalyses(50);
          if (res.success && res.data) {
            setAnalyses(res.data.analyses || []);
          }
          break;
        }
      }
    } catch (err: any) {
      console.error(`[Content] load ${tab} failed:`, err);
      const msg = err?.message || '加载失败，请稍后重试';
      setLoadError((prev) => ({ ...prev, [tab]: msg }));
    } finally {
      setLoading((prev) => ({ ...prev, [tab]: false }));
    }
  }, []);

  useEffect(() => {
    loadData(activeTab);
  }, [activeTab, loadData]);

  // 页面重新获得焦点时自动刷新数据（方便从聊天页操作后切回查看）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData(activeTab);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeTab, loadData]);

  const formatNumber = (num?: number) => {
    if (!num) return '0';
    if (num >= 10000) return (num / 10000).toFixed(1) + 'w';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toString();
  };

  // ==================== 热门视频批量操作 ====================
  const toggleSelectVideo = (id: string) => {
    setSelectedVideos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVideos = () => {
    if (selectedVideos.size === hotVideos.length) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(hotVideos.map((v) => v.id)));
    }
  };

  const handleBatchExtract = async () => {
    if (selectedVideos.size === 0) return;
    const selected = hotVideos.filter((v) => selectedVideos.has(v.id));
    const videos = selected
      .map((v) => {
        if (v.aweme_id) return { awemeId: v.aweme_id };
        if (v.share_url) return { url: v.share_url };
        return null;
      })
      .filter(Boolean) as { url?: string; awemeId?: string }[];

    if (videos.length === 0) return;

    setBatchExtracting(true);
    try {
      const res = await videoAPI.batchExtractLeads(videos, 100);
      if (res.success && res.data?.taskId) {
        // 轮询任务状态
        const taskId = res.data.taskId;
        const maxAttempts = 600;
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          const statusRes = await videoAPI.getTaskStatus(taskId);
          if (!statusRes.success || !statusRes.data) continue;
          const task = statusRes.data;
          if (task.status === 'completed') {
            alert(`批量提炼完成：共 ${task.result?.leads?.length || 0} 条潜在客户评论`);
            // 刷新 leads tab
            loadData('leads');
            break;
          } else if (task.status === 'failed') {
            alert('批量提炼失败：' + (task.error || '未知错误'));
            break;
          }
        }
      } else {
        alert('创建任务失败');
      }
    } catch (err: any) {
      alert('批量提炼出错：' + err?.message);
    } finally {
      setBatchExtracting(false);
      setSelectedVideos(new Set());
    }
  };

  // ==================== 渲染辅助 ====================
  const renderEmpty = (text: string) => (
    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
      <div className="text-4xl mb-3">📭</div>
      <p>{text}</p>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-y-auto min-w-0">
        {/* 顶部导航 */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 h-14 flex items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>返回</span>
            </button>
            <h1 className="ml-4 text-lg font-semibold">内容库</h1>
          </div>
          <div className="max-w-6xl mx-auto px-4 pb-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">管理和查看所有已保存的数据</p>
            <div className="text-sm text-gray-500">
              当前用户：<span className="font-medium text-gray-700">{user?.username}</span>
            </div>
          </div>

          {/* Tab 切换 */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSearchParams({ tab: tab.key });
                  }}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* 主内容 */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* 工具栏 */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-500">
            {activeTab === 'similar' && `共 ${similarUsers.length} 个同类账号`}
            {activeTab === 'hot' && `共 ${hotVideos.length} 个热门视频`}
            {activeTab === 'leads' && `共 ${leads.length} 条提炼记录`}
            {activeTab === 'analysis' && `共 ${analyses.length} 条分析记录`}
            {activeTab === 'sessions' && `共 ${sessions.length} 个会话`}
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'hot' && (
              <>
                <button
                  onClick={toggleSelectAllVideos}
                  disabled={hotVideos.length === 0 || batchExtracting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {selectedVideos.size === hotVideos.length && hotVideos.length > 0 ? (
                    <CheckSquare size={14} />
                  ) : (
                    <Square size={14} />
                  )}
                  全选
                </button>
                <button
                  onClick={handleBatchExtract}
                  disabled={selectedVideos.size === 0 || batchExtracting}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors',
                    selectedVideos.size > 0 && !batchExtracting
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  )}
                >
                  {batchExtracting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Sparkles size={14} />
                  )}
                  批量评论提炼 ({selectedVideos.size})
                </button>
              </>
            )}
            <button
              onClick={() => loadData(activeTab)}
              disabled={loading[activeTab]}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={14} className={cn(loading[activeTab] && 'animate-spin')} />
              刷新
            </button>
          </div>
        </div>

        {/* 同类账号 */}
        {activeTab === 'similar' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading.similar ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-gray-400" size={24} />
              </div>
            ) : similarUsers.length === 0 ? (
              renderEmpty('暂无同类账号，请先在聊天页使用"获取同类"功能')
            ) : (
              <div className="divide-y divide-gray-100">
                {similarUsers.map((u, idx) => (
                  <div key={u.id} className="p-4 flex items-start gap-4 hover:bg-gray-50">
                    <div className="text-sm text-gray-400 w-6 text-center">{idx + 1}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{u.nickname || '未知用户'}</span>
                        <a
                          href={`https://www.douyin.com/user/${u.sec_uid}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink size={14} />
                        </a>
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex gap-4">
                        <span>粉丝 {formatNumber(u.follower_count)}</span>
                        <span>作品 {formatNumber(u.aweme_count)}</span>
                      </div>
                      {u.signature && (
                        <div className="text-sm text-gray-600 mt-1 line-clamp-1">{u.signature}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 热门视频 */}
        {activeTab === 'hot' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading.hot ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-gray-400" size={24} />
              </div>
            ) : loadError.hot ? (
              <div className="flex flex-col items-center justify-center py-16 text-red-500">
                <div className="text-4xl mb-3">⚠️</div>
                <p>{loadError.hot}</p>
              </div>
            ) : hotVideos.length === 0 ? (
              renderEmpty('暂无热门视频，请先在聊天页使用"同类热门"功能')
            ) : (
              <div className="divide-y divide-gray-100">
                {hotVideos.map((v) => (
                  <div key={v.id} className="p-4 flex items-start gap-4 hover:bg-gray-50">
                    <button
                      onClick={() => toggleSelectVideo(v.id)}
                      className="mt-0.5 text-gray-400 hover:text-blue-600"
                    >
                      {selectedVideos.has(v.id) ? (
                        <CheckSquare size={18} className="text-blue-600" />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {v.title || v.desc || '无标题'}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-3">
                        <span className="flex items-center gap-1">
                          <Play size={14} /> {formatNumber(v.play_count)}
                        </span>
                        <span className="flex items-center gap-1">
                          <ThumbsUp size={14} /> {formatNumber(v.digg_count)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle size={14} /> {formatNumber(v.comment_count)}
                        </span>
                        <span className="text-gray-400">·</span>
                        <span>sec_uid: {v.sec_uid?.slice(0, 12)}...</span>
                      </div>
                      {v.share_url && (
                        <a
                          href={v.share_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-2"
                        >
                          <ExternalLink size={14} />
                          打开视频
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 有效评论 */}
        {activeTab === 'leads' && (
          <div className="space-y-4">
            {loading.leads ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-gray-400" size={24} />
              </div>
            ) : loadError.leads ? (
              <div className="flex flex-col items-center justify-center py-16 text-red-500">
                <div className="text-4xl mb-3">⚠️</div>
                <p>{loadError.leads}</p>
              </div>
            ) : leads.length === 0 ? (
              renderEmpty('暂无评论提炼记录，请先在聊天页使用"评论提炼"功能')
            ) : (
              leads.map((record) => {
                const isExpanded = expandedLeads.has(record.id);
                const comments = record.display_comments || record.all_comments || record.leads || [];
                const leadCount = (record.all_comments || record.leads || []).filter((c: any) => c.is_lead).length;
                return (
                  <div key={record.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">
                          {record.video_title || (record.aweme_id ? (
                            <a
                              href={`https://www.douyin.com/video/${record.aweme_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              打开视频
                            </a>
                          ) : '未知视频')}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          共 {comments.length} 条评论，{leadCount} 条潜在客户 · {formatTime(record.created_at)}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setExpandedLeads((prev) => {
                            const next = new Set(prev);
                            if (next.has(record.id)) next.delete(record.id);
                            else next.add(record.id);
                            return next;
                          })
                        }
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        {isExpanded ? '收起' : '展开'}
                      </button>
                    </div>
                    {record.summary && (
                      <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                        {record.summary}
                      </div>
                    )}
                    {isExpanded && (
                      <div className="mt-3 space-y-2">
                        {comments.slice(0, 50).map((c: any, i: number) => (
                          <div
                            key={i}
                            className={cn(
                              'text-sm pl-3 py-1 rounded-r',
                              c.is_lead
                                ? 'border-l-4 border-red-400 bg-red-50'
                                : 'border-l-4 border-gray-200 bg-gray-50'
                            )}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              {c.avatar ? (
                                <img src={c.avatar} alt="" className="w-5 h-5 rounded-full" />
                              ) : null}
                              {c.profile_url || c.sec_uid ? (
                                <a
                                  href={c.profile_url || `https://www.douyin.com/user/${c.sec_uid}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-medium text-blue-600 hover:text-blue-700"
                                >
                                  @{c.nickname || c.username || '匿名'}
                                </a>
                              ) : (
                                <span className="font-medium text-gray-900">
                                  @{c.nickname || c.username || '匿名'}
                                </span>
                              )}
                              {c.is_lead && (
                                <>
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                    {c.type || '未知'}
                                  </span>
                                  {c.priority && (
                                    <span
                                      className={cn(
                                        'text-xs px-1.5 py-0.5 rounded',
                                        c.priority === 'high'
                                          ? 'bg-red-100 text-red-700'
                                          : c.priority === 'medium'
                                          ? 'bg-yellow-100 text-yellow-700'
                                          : 'bg-gray-100 text-gray-600'
                                      )}
                                    >
                                      {c.priority}
                                    </span>
                                  )}
                                  <span className="text-xs text-red-500 font-medium">潜在客户</span>
                                </>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">ID: {c.comment_id || 'N/A'}</div>
                            <div className="text-gray-700 mt-0.5">{c.content}</div>
                            {c.is_lead && c.intent && (
                              <div className="text-gray-500 mt-0.5">💡 {c.intent}</div>
                            )}
                          </div>
                        ))}
                        {comments.length > 50 && (
                          <div className="text-sm text-gray-400">
                            还有 {comments.length - 50} 条未显示
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* 会话记录 */}
        {activeTab === 'sessions' && (
          <div className="space-y-4">
            {sessions.length === 0 ? (
              renderEmpty('暂无会话记录')
            ) : (
              sessions.map((session) => {
                const isExpanded = expandedSessions.has(session.id);
                return (
                  <div key={session.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{session.title}</div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          {session.messages.length} 条消息 · {formatTime(session.updated_at)}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setExpandedSessions((prev) => {
                            const next = new Set(prev);
                            if (next.has(session.id)) next.delete(session.id);
                            else next.add(session.id);
                            return next;
                          })
                        }
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        {isExpanded ? '收起' : '展开'}
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="mt-3 divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                        {session.messages.length === 0 ? (
                          <div className="p-4 text-sm text-gray-400">该会话暂无消息</div>
                        ) : (
                          session.messages.map((message) => (
                            <Message key={message.id} message={message} />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* 评论分析 */}
        {activeTab === 'analysis' && (
          <div className="space-y-4">
            {loading.analysis ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-gray-400" size={24} />
              </div>
            ) : loadError.analysis ? (
              <div className="flex flex-col items-center justify-center py-16 text-red-500">
                <div className="text-4xl mb-3">⚠️</div>
                <p>{loadError.analysis}</p>
              </div>
            ) : analyses.length === 0 ? (
              renderEmpty('暂无评论分析记录，请先在聊天页使用"评论分析"功能')
            ) : (
              analyses.map((item) => {
                const isExpanded = expandedAnalyses.has(item.id);
                const analysis = item.analysis || {};
                const detailed = (item.detailed_analysis || [])[0];
                const isSingleComment = detailed && (detailed.content || detailed.replies_count !== undefined);
                
                return (
                  <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">
                          {isSingleComment ? `单条评论分析` : `分析 #${item.id.slice(0, 8)}`}
                          {item.aweme_id && (
                            <>
                              {' · '}
                              <a
                                href={`https://www.douyin.com/video/${item.aweme_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                打开视频
                              </a>
                            </>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          {isSingleComment ? (
                            <>
                              情感 {analysis.sentiment || '未知'} · {detailed.replies_count || 0} 条回复
                              {detailed.content && (
                                <span className="block text-gray-400 mt-0.5 truncate max-w-md">
                                  原文：{detailed.content}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {analysis?.total_leads !== undefined && `线索 ${analysis.total_leads} 条 · `}
                              高意向 {analysis?.high_priority || 0} · 中意向 {analysis?.medium_priority || 0} · 低意向 {analysis?.low_priority || 0}
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setExpandedAnalyses((prev) => {
                            const next = new Set(prev);
                            if (next.has(item.id)) next.delete(item.id);
                            else next.add(item.id);
                            return next;
                          })
                        }
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        {isExpanded ? '收起' : '展开'}
                      </button>
                    </div>

                    {item.strategy && (
                      <div className="mt-3 text-sm text-gray-700 bg-green-50 rounded-lg p-3">
                        <div className="font-medium text-green-800 mb-1">💡 整体策略</div>
                        {item.strategy}
                      </div>
                    )}

                    {isExpanded && (
                      <div className="mt-3 space-y-3">
                        {isSingleComment ? (
                          <>
                            <div className="text-sm border-l-4 border-blue-300 pl-3 py-1">
                              <div className="text-gray-700 font-medium">目标评论</div>
                              {detailed.user_sec_uid ? (
                                <a
                                  href={`https://www.douyin.com/user/${detailed.user_sec_uid}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 hover:text-blue-700 text-xs"
                                >
                                  @{detailed.username || '匿名'}
                                </a>
                              ) : (
                                <div className="text-gray-500 text-xs">@{detailed.username || '匿名'}</div>
                              )}
                              <div className="text-xs text-gray-400 mt-0.5">ID: {detailed.comment_id || 'N/A'}</div>
                              <div className="text-gray-600 mt-0.5">{detailed.content}</div>
                            </div>
                            
                            <div className="text-sm bg-gray-50 rounded-lg p-3">
                              <div className="font-medium text-gray-900 mb-1">分析结论</div>
                              <div className="text-gray-600">• 情感倾向：{analysis.sentiment === 'positive' ? '正面 😊' : analysis.sentiment === 'negative' ? '负面 😤' : '中性 😐'}</div>
                              <div className="text-gray-600">• 用户意图：{analysis.intent || '未知'}</div>
                              {(analysis.key_points?.length ?? 0) > 0 && (
                                <div className="text-gray-600">• 关键要点：{(analysis.key_points || []).join('、')}</div>
                              )}
                              {analysis.user_profile && (
                                <div className="text-gray-600">• 用户画像：{analysis.user_profile}</div>
                              )}
                            </div>
                            
                            {detailed.reply_content && (
                              <div className="text-sm bg-green-50 rounded-lg p-3">
                                <div className="font-medium text-green-800 mb-1">🤖 AI 回复建议</div>
                                <div className="text-gray-700">{detailed.reply_content}</div>
                                <div className="text-gray-500 mt-1 text-xs">
                                  语气：{detailed.reply_tone || '友好'} · 策略：{detailed.reply_strategy || '直接回复'}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {(item.detailed_analysis || []).slice(0, 10).map((d: any, i: number) => (
                              <div
                                key={i}
                                className="text-sm border-l-4 border-green-300 pl-3 py-1"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    @{d.username || '匿名'}
                                  </span>
                                  <span
                                    className={cn(
                                      'text-xs px-1.5 py-0.5 rounded',
                                      d.conversion_potential === 'high'
                                        ? 'bg-red-100 text-red-700'
                                        : d.conversion_potential === 'medium'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-600'
                                    )}
                                  >
                                    {d.conversion_potential || '未知'}
                                  </span>
                                  {d.intent_score !== undefined && (
                                    <span className="text-xs text-gray-500">
                                      意向分 {d.intent_score}/100
                                    </span>
                                  )}
                                </div>
                                {d.suggested_reply && (
                                  <div className="text-gray-600 mt-1">
                                    💬 {d.suggested_reply}
                                  </div>
                                )}
                                {d.pain_points && d.pain_points.length > 0 && (
                                  <div className="text-gray-500 mt-0.5">
                                    痛点：{d.pain_points.join('、')}
                                  </div>
                                )}
                              </div>
                            ))}
                            {(item.detailed_analysis || []).length > 10 && (
                              <div className="text-sm text-gray-400">
                                还有 {(item.detailed_analysis || []).length - 10} 条未显示
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      </main>
    </div>
  );
}
